import {
    Action,
    getModule,
    Module,
    Mutation,
    VuexModule,
} from 'vuex-module-decorators';
import { newRightsMap } from '~/constants/rightsMap.constant';
import { ListRights, OperatorState, Privilege, Rights } from '~/interfaces/operator.interface';
import router from '~/router';
import apiService from '~/services/api.service';
import httpService from '~/services/http.service';
import { ConfigModule } from '~/store/modules/config';
import store from '~common-store';
import { LoadingModule } from '~common-store/modules/loading';
@Module({ dynamic: true, store, name: 'operator', namespaced: true })
class Operator extends VuexModule implements OperatorState {
    isSuperModerator = false;
    isModerator = false;
    rights: Rights = {};
    mail = '';

    rawRights: Record<string, ListRights> = {};
    categoriesByScope: Record<string, string[]> = {};

    get adminRights(): Rights {
        return this.rights;
    }

    get adminRawRights(): Record<string, ListRights> {
        return this.rawRights;
    }

    get adminMail(): string {
        return this.mail;
    }

    get operatorDeviceTypes(): string[] {
        return Object.keys(this.rights).filter((right) => right !== 'moderator');
    }

    get adminScopeCategories(): Record<string, string[]> {
        return this.categoriesByScope;
    }

    get isAllowed(): (scope: string, action?: string) => boolean {
        return ((scope: string, action?: string) => {
            const operatorDeviceRights = this.rights[scope];
            if (!operatorDeviceRights) {
                return false;
            }
            return !action || operatorDeviceRights.includes(action);
        });

    }

    // https://netatmo.atlassian.net/wiki/spaces/ADMIN/pages/1735589919/Support+DGM+rights+v3#Frontend-logic%3A
    // detailed explanation of the rights
    @Action
    async getAdminRights(): Promise<never[] | undefined> {
        try {
            LoadingModule.showLoading(true);

            const response = await apiService.admingetadminrights();

            const rights = response.data.apps[ConfigModule.appName];

            if (rights) {
                const mappedPrivileges = (rights.privileges as Privilege[]).reduce((objAccumulator, entry) => {
                    return {
                        ...objAccumulator,
                        [entry.scope.name]: {
                            listRights: entry.rights.map(right => right.name),
                            type: entry.scope.type || 'gateway_type',
                        },
                    };
                }, {});

                const categoriesOperatorScope = rights.privileges.reduce((objAccumulator: Record<string, string[]>, entry: Privilege) => {
                    return {
                        ...objAccumulator,
                        [entry.scope.name]: [
                            ...objAccumulator[entry.scope.name] || [],
                            entry?.category?.environment,
                        ],
                    };
                }, {});

                this.SET_RAW_RIGHTS(mappedPrivileges);
                this.SET_OPERATOR_RIGHTS(newGetAvailableActionsFromRights(mappedPrivileges));
                this.SET_MODERATOR(rights.is_moderator);
                this.SET_ADMIN_SCOPE_CATEGORIES(categoriesOperatorScope);
            } else {
                this.SET_OPERATOR_RIGHTS({});
                this.SET_ADMIN_SCOPE_CATEGORIES({});
                this.SET_MODERATOR(false);
            }
            this.SET_MAIL(response?.data?.email);

            LoadingModule.showLoading(false);

        } catch (error) {
            return [];
        } finally {
            LoadingModule.showLoading(false);
        }
    }

    @Action
    logOut(): void {
        delete httpService.defaults.headers.common.Authorization;
        router.push({
            name: `${ConfigModule.supportAppNameBase}-redirect`,
            query: {
                url: ConfigModule.logOutUrl,
            },
        });
    }

    @Action
    async refreshToken(): Promise<void> {
        // wait 2000ms before redirecting
        await new Promise((r) => setTimeout(r, 2000));
        window.location.href = `${ConfigModule.checkLoginUrl}?next_url=${encodeURIComponent(window.location.href)}`;
    }

    @Mutation
    SET_OPERATOR_RIGHTS(rights: { [scope: string]: string[] }): void {
        this.rights = rights;
    }

    @Mutation
    SET_SUPER_MODERATOR(isSuperModerator: boolean): void {
        this.isSuperModerator = isSuperModerator;
    }

    @Mutation
    SET_MODERATOR(isModerator: boolean): void {
        this.isModerator = isModerator;
    }

    @Mutation
    SET_MAIL(mail: string): void {
        this.mail = mail;
    }

    @Mutation
    SET_RAW_RIGHTS(rights: Record<string, ListRights>): void {
        this.rawRights = rights;
    }

    @Mutation
    SET_ADMIN_SCOPE_CATEGORIES(categories: Record<string, string[]>): void {
        this.categoriesByScope = categories;
    }

}

function newGetAvailableActionsFromRights(rights: Record<string, ListRights>): any {
    return Object.entries(rights).reduce((objAccumulator, [scope, scopeRights]) => ({
        ...objAccumulator,
        [scope]: scopeRights.listRights.map((right: string) => {
            const commonRights = newRightsMap.common?.[scopeRights.type]?.[right] || [];
            const specificRights = newRightsMap.specific?.[scope]?.[right] || [];
            return [...commonRights, ...specificRights];
        }).flat(),
    }), {});
}

export const OperatorModule = getModule(Operator);
